import { useEffect, useId, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/common/Button';
import { codiceInvito, leggiCodiceInvito } from '@/lib/gamehost/rest';
import { motivoConflittoOf, POSTI_SCOPA, POSTI_SCOPONE, type MotivoConflitto, type PostiScopa } from '@/lib/scopa/gioco';
import { LIVELLI_ROBOT, eRobotNonDisponibili, type LivelloRobot } from '@/lib/scopa/robot';
import { useAzioneTavolo, type GuastoAzione } from '@/lib/gamehost/useAzioneTavolo';
import { scopaApi, useCreateScopaTableMutation, useJoinScopaTableMutation } from '@/services/limuloScopa';
import { useAppDispatch } from '@/store/hooks';

type Azione = 'gioca' | 'invita' | 'entra' | 'robot';
type Guasto = GuastoAzione | 'codiceNonValido' | 'robotNonDisponibili';

/** La frase di un guasto: una chiave di una lista chiusa sotto `hub.gioca.guasto.`. Il testo del server non si mostra MAI. */
function chiaveGuasto(azione: Azione, g: Guasto): string {
  switch (g) {
    case 'non-disponibile':
      return 'nonDisponibile';
    case 'conflitto':
      return azione === 'entra' ? 'conflittoEntra' : 'conflittoCrea';
    case 'nonTrovato':
      return azione === 'entra' ? 'nonTrovato' : 'errore';
    default:
      return g;
  }
}

/** Il 409 di un INGRESSO letto per motivo; `seduto` (solo per `gia-seduto`): il tavolo VIVO dove siede, dal server (`tables/mine`) — `qui` se e quello del codice. `null` = non letto. */
interface ConflittoEntra {
  motivo: MotivoConflitto;
  seduto: { tableId: string; qui: boolean } | null;
}

/** La frase di un 409 sull'ingresso: una chiave di `hub.gioca.guasto.`. «Gia seduto» senza sapere DOVE (l'elenco non si legge) resta la frase di sempre, che lo dice fra le due ipotesi. */
function chiaveConflitto(c: ConflittoEntra): string {
  if (c.motivo === 'pieno') return 'pieno';
  if (c.motivo === 'in-volo') return 'inVolo';
  return c.seduto === null ? 'conflittoEntra' : c.seduto.qui ? 'giaSedutoQui' : 'giaSedutoAltrove';
}

/**
 * «GIOCA», «CONTRO I ROBOT», «INVITA UN AMICO» ed «ENTRA CON UN CODICE» nella Hub della scopa (sul modello di `GiocaBurraco`, lo stesso core): le SCRITTURE, ognuna con la sua
 * chiave di idempotenza (`useAzioneTavolo`: il doppio tocco non crea due tavoli). Prima si sceglie il GIOCO: la scopa (2 posti, uno contro uno) o lo scopone scientifico (4 posti,
 * coppie a posti alterni). ⚠️ Le risposte sono ricavate dal codice, non misurate (#245 non e ancora servita): v. `lib/gamehost/rest.ts`.
 *  · «Gioca»: un tavolo casual (`scopa.table.create`), apre `/scopa/tavolo/:id`. Chi completa il tavolo lo decide il game host.
 *  · «Contro i robot»: un tavolo casual col campo `robot: { livello }` (L0 Principiante, L1 Intermedio, L2 Esperto). Chi crea siede a un posto estratto dal core e OGNI altro posto
 *    e un robot del pool, col badge «bot»: nello scopone sono tre. Senza account nel pool il tavolo non nasce (503 `no_robot_available`, riconosciuto dallo status E dal codice):
 *    si dice in chiaro; un altro 503 resta un guasto di servizio. ⚠️ Il campo `robot` arriva al game host solo con il mirror 0.2.x di app-qscopa su Limulo staging (kb#432) e con
 *    `robotAllaNascita` acceso in QScopa (I-1017).
 *  · «Invita un amico»: un tavolo privato; mostra il CODICE da condividere e NON entra da solo (l'invito si vede una volta sola).
 *  · «Entra con un codice»: `scopa.table.join` col `tableId` e l'invito letti dal codice. Un codice che non ha la forma non parte nemmeno. Un 409 si dice per MOTIVO (kb#476): «gia seduto»
 *    (con «Riprendi» verso il tavolo dove si siede DAVVERO, letto da `tables/mine` al momento: quello del codice, o un altro), «pieno», «stai gia entrando»; ogni altro 409 e la frase di sempre.
 * Il codice e una credenziale: sta SOLO nello stato del componente, mai in storage, in log o nell'URL. Nessun tavolo «in classifica»: la classifica e S7.
 *
 * 🔴 Ogni azione ha una chiave di idempotenza PER CORPO: «Gioca», «Invita» e ognuno dei livelli dei robot, per gioco, sono corpi diversi, e una chiave rimasta da un guasto
 * incerto della scopa riusata per lo scopone (o per un altro livello) prende 409 `idempotency_key_reused` dal core (revisione del seggio su #735, punto 5). Tornando all'azione
 * di prima si ritrova la SUA chiave: ripetere la stessa azione dopo un esito incerto ritrova lo stesso tavolo, che e il motivo della chiave.
 *
 * COPIA DICHIARATA della forma di «Contro i robot» di `GiocaBriscola` (TMA#746 @ e823c650454b6adf11046bffc675774d6429937d, `src/components/briscola/GiocaBriscola.tsx`, origin/main
 * 47da9d56): righe 13-14 (i tipi `Azione` e `Guasto`), 56-59 e 68-71 (un'azione per livello e per formato), 85-100 (`controIRobot`), 129-143 (il guasto che si mostra) e 162-172
 * (il blocco dei tre livelli). Differenze, tutte volute: `useAzioneTavolo` del trasporto comune (la zona della scopa non importa dalla briscola); i due formati sono
 * la scopa e lo scopone; il campo `robot` lo mette `creaTavoloBody` SOLO per un casual. Nessun import dalla zona della briscola.
 */
export function GiocaScopa() {
  const { t } = useTranslation('scopa');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const titoloId = useId();
  const [crea] = useCreateScopaTableMutation();
  const [entra] = useJoinScopaTableMutation();
  const aGiocaScopa = useAzioneTavolo<{ tableId: string }>();
  const aGiocaScopone = useAzioneTavolo<{ tableId: string }>();
  const aInvitaScopa = useAzioneTavolo<{ tableId: string; codice: string | null }>();
  const aInvitaScopone = useAzioneTavolo<{ tableId: string; codice: string | null }>();
  const aRobotScopa = { L0: useAzioneTavolo<{ tableId: string }>(), L1: useAzioneTavolo<{ tableId: string }>(), L2: useAzioneTavolo<{ tableId: string }>() };
  const aRobotScopone = { L0: useAzioneTavolo<{ tableId: string }>(), L1: useAzioneTavolo<{ tableId: string }>(), L2: useAzioneTavolo<{ tableId: string }>() };
  const aEntra = useAzioneTavolo<{ tableId: string }>();
  const [posti, setPosti] = useState<PostiScopa>(POSTI_SCOPA);
  const aGioca = posti === POSTI_SCOPA ? aGiocaScopa : aGiocaScopone;
  const aInvita = posti === POSTI_SCOPA ? aInvitaScopa : aInvitaScopone;
  const aRobot = posti === POSTI_SCOPA ? aRobotScopa : aRobotScopone;
  const [invitoPronto, setInvitoPronto] = useState<{ tableId: string; codice: string | null } | null>(null);
  const [testo, setTesto] = useState('');
  const [codiceStorto, setCodiceStorto] = useState(false);
  const [ultima, setUltima] = useState<Azione | null>(null);
  const [livelloUltimo, setLivelloUltimo] = useState<LivelloRobot>('L0');
  const [senzaRobot, setSenzaRobot] = useState(false);
  const [conflitto, setConflitto] = useState<ConflittoEntra | null>(null);
  const occupato = [aGiocaScopa, aGiocaScopone, aInvitaScopa, aInvitaScopone, aEntra, ...LIVELLI_ROBOT.flatMap((l) => [aRobotScopa[l], aRobotScopone[l]])].some((a) => a.stato.invio);

  const vaiAlTavolo = (tableId: string) => navigate(`/scopa/tavolo/${tableId}`);

  /** DOVE siede chi ha preso `already_seated`: i tavoli VIVI dal server, riletti adesso (non una copia vecchia). Elenco illeggibile o vuoto: `null`, e la frase non promette un tavolo. */
  const doveSiede = async (tableId: string): Promise<{ tableId: string; qui: boolean } | null> => {
    const richiesta = dispatch(scopaApi.endpoints.getMyScopaTables.initiate(undefined, { forceRefetch: true }));
    try {
      const elenco = await richiesta.unwrap();
      if (!elenco.ok || elenco.tavoli.length === 0) return null;
      const questo = elenco.tavoli.find((x) => x.tableId === tableId);
      return questo !== undefined ? { tableId: questo.tableId, qui: true } : { tableId: elenco.tavoli[0]!.tableId, qui: false };
    } catch {
      return null;
    } finally {
      richiesta.unsubscribe();
    }
  };

  const gioca = async () => {
    setUltima('gioca');
    setInvitoPronto(null);
    const r = await aGioca.esegui(async (idempotencyKey) => {
      const tavolo = await crea({ tipo: 'casual', posti, idempotencyKey }).unwrap();
      return tavolo === null ? null : { tableId: tavolo.tableId };
    });
    if (r !== null) vaiAlTavolo(r.tableId);
  };

  const controIRobot = async (livello: LivelloRobot) => {
    setUltima('robot');
    setLivelloUltimo(livello);
    setSenzaRobot(false);
    setInvitoPronto(null);
    const r = await aRobot[livello].esegui(async (idempotencyKey) => {
      try {
        const tavolo = await crea({ tipo: 'casual', posti, idempotencyKey, livello }).unwrap();
        return tavolo === null ? null : { tableId: tavolo.tableId };
      } catch (e) {
        if (eRobotNonDisponibili(e)) setSenzaRobot(true);
        throw e;
      }
    });
    if (r !== null) vaiAlTavolo(r.tableId);
  };

  const invita = async () => {
    setUltima('invita');
    setInvitoPronto(null);
    const r = await aInvita.esegui(async (idempotencyKey) => {
      const tavolo = await crea({ tipo: 'privato', posti, idempotencyKey }).unwrap();
      return tavolo === null ? null : { tableId: tavolo.tableId, codice: tavolo.invito === null ? null : codiceInvito(tavolo.tableId, tavolo.invito) };
    });
    if (r !== null) setInvitoPronto(r);
  };

  const entraCol = async (e: FormEvent) => {
    e.preventDefault();
    setUltima('entra');
    setInvitoPronto(null);
    setConflitto(null);
    const letto = leggiCodiceInvito(testo);
    setCodiceStorto(letto === null);
    if (letto === null) return; // niente richiesta per un codice che non ha la forma
    const r = await aEntra.esegui(async (idempotencyKey) => {
      try {
        const ingresso = await entra({ tableId: letto.tableId, invito: letto.invito, idempotencyKey }).unwrap();
        return ingresso === null ? null : { tableId: ingresso.tableId };
      } catch (errore) {
        // il motivo del 409 e la lettura di DOVE si siede stanno dentro la richiesta: i bottoni restano spenti finche la frase non e pronta
        const motivo = motivoConflittoOf(errore);
        if (motivo !== null) setConflitto({ motivo, seduto: motivo === 'gia-seduto' ? await doveSiede(letto.tableId) : null });
        throw errore;
      }
    });
    if (r !== null) {
      setTesto('');
      vaiAlTavolo(r.tableId);
    }
  };

  const guastoRobot = aRobot[livelloUltimo].stato.guasto;
  const guasto: Guasto | null =
    ultima === 'entra' && codiceStorto
      ? 'codiceNonValido'
      : ultima === 'gioca'
        ? aGioca.stato.guasto
        : ultima === 'invita'
          ? aInvita.stato.guasto
          : ultima === 'robot'
            ? senzaRobot && guastoRobot === 'server'
              ? 'robotNonDisponibili'
              : guastoRobot
            : ultima === 'entra'
              ? aEntra.stato.guasto
              : null;

  const conflittoVisto = ultima === 'entra' && guasto === 'conflitto' ? conflitto : null;

  return (
    <section data-testid="scopa-gioca" aria-labelledby={titoloId} className="flex shrink-0 flex-col gap-3">
      <h2 id={titoloId} className="text-xs font-bold uppercase tracking-wide text-fg-muted">
        {t('hub.gioca.titolo')}
      </h2>
      <div className="grid grid-cols-2 gap-2" data-scelta-gioco>
        <Button variant="scelta" forma="tessera" pressed={posti === POSTI_SCOPA} data-posti={POSTI_SCOPA} disabled={occupato} onClick={() => setPosti(POSTI_SCOPA)}>
          {t('modo.scopa')}
        </Button>
        <Button variant="scelta" forma="tessera" pressed={posti === POSTI_SCOPONE} data-posti={POSTI_SCOPONE} disabled={occupato} onClick={() => setPosti(POSTI_SCOPONE)}>
          {t('modo.scopone')}
        </Button>
      </div>
      <p className="px-1 text-xs text-fg-muted">{posti === POSTI_SCOPA ? t('modo.scopaNota') : t('modo.scoponeNota')}</p>
      <Button variant="primary" fullWidth data-azione="gioca" disabled={occupato} onClick={() => void gioca()}>
        {aGioca.stato.invio ? t('hub.gioca.invio') : t('hub.gioca.gioca')}
      </Button>
      <div data-robot className="flex flex-col gap-2">
        <p className="text-sm font-semibold text-fg">{t('hub.gioca.robot.titolo')}</p>
        <div className="grid grid-cols-3 gap-2">
          {LIVELLI_ROBOT.map((livello) => (
            <Button key={livello} variant="secondary" data-azione="robot" data-livello={livello} disabled={occupato} onClick={() => void controIRobot(livello)}>
              {aRobot[livello].stato.invio ? t('hub.gioca.invio') : t(`livello.${livello}`)}
            </Button>
          ))}
        </div>
        <p className="px-1 text-xs text-fg-muted">{posti === POSTI_SCOPA ? t('hub.gioca.robot.nota') : t('hub.gioca.robot.notaDue')}</p>
      </div>
      <div className="flex flex-col gap-1">
        <Button variant="secondary" fullWidth data-azione="invita" disabled={occupato} onClick={() => void invita()}>
          {aInvita.stato.invio ? t('hub.gioca.invio') : t('hub.gioca.invita')}
        </Button>
        <p className="px-1 text-xs text-fg-muted">{t('hub.gioca.invitaNota')}</p>
      </div>
      {invitoPronto !== null && <InvitoPronto pronto={invitoPronto} onVai={() => vaiAlTavolo(invitoPronto.tableId)} />}
      <form data-azione="entra" onSubmit={(e) => void entraCol(e)} className="flex flex-col gap-2">
        <label htmlFor={`${titoloId}-codice`} className="text-sm font-semibold text-fg">
          {t('hub.gioca.entra.titolo')}
        </label>
        <input
          id={`${titoloId}-codice`}
          type="text"
          value={testo}
          onChange={(e) => {
            setTesto(e.target.value);
            setCodiceStorto(false);
          }}
          placeholder={t('hub.gioca.entra.segnaposto')}
          autoCapitalize="none"
          autoCorrect="off"
          autoComplete="off"
          spellCheck={false}
          disabled={occupato}
          className="min-h-11 w-full min-w-0 rounded-xl border border-border bg-bg-secondary px-3 text-sm text-fg placeholder:text-fg-muted"
        />
        <Button type="submit" variant="secondary" fullWidth disabled={occupato || testo.trim() === ''}>
          {aEntra.stato.invio ? t('hub.gioca.invio') : t('hub.gioca.entra.azione')}
        </Button>
      </form>
      {/* «Stai gia entrando» e un'attesa, non un guasto: tono neutro (tma-ui-orcb, kb#476) */}
      {guasto !== null && (
        <p
          role="status"
          data-gioca-guasto={guasto}
          data-gioca-conflitto={conflittoVisto?.motivo}
          className={`rounded-xl bg-bg-secondary px-4 py-3 text-sm ${conflittoVisto?.motivo === 'in-volo' ? 'text-fg-muted' : 'text-danger'}`}
        >
          {t(`hub.gioca.guasto.${conflittoVisto !== null ? chiaveConflitto(conflittoVisto) : chiaveGuasto(ultima ?? 'gioca', guasto)}`)}
        </p>
      )}
      {conflittoVisto?.seduto && (
        <Button variant="secondary" fullWidth data-azione="riprendi" data-seduto-qui={conflittoVisto.seduto.qui} onClick={() => vaiAlTavolo(conflittoVisto.seduto!.tableId)}>
          {t('hub.tavoli.riprendi')}
        </Button>
      )}
    </section>
  );
}

/** Il codice da condividere, copiabile. «Copiato» si dice SOLO se la copia e riuscita; a copia fallita il codice si seleziona con un tocco lungo. */
function InvitoPronto({ pronto, onVai }: { pronto: { tableId: string; codice: string | null }; onVai: () => void }) {
  const { t } = useTranslation('scopa');
  const [esito, setEsito] = useState<'ok' | 'ko' | null>(null);
  useEffect(() => setEsito(null), [pronto.codice]);
  const copia = async () => {
    if (pronto.codice === null) return;
    try {
      await navigator.clipboard.writeText(pronto.codice);
      setEsito('ok');
    } catch {
      setEsito('ko');
    }
  };
  return (
    <div data-scopa-invito-pronto className="card-edge flex flex-col gap-2 rounded-xl bg-bg-secondary p-3">
      {pronto.codice === null ? (
        <p data-scopa-invito-assente className="text-sm text-fg-muted">
          {t('hub.gioca.codice.assente')}
        </p>
      ) : (
        <>
          <p className="text-sm font-semibold text-fg">{t('hub.gioca.codice.titolo')}</p>
          <code data-scopa-invito-codice className="select-all break-all rounded bg-bg-tertiary px-2 py-2 font-mono text-xs text-fg">
            {pronto.codice}
          </code>
          <p className="text-xs text-fg-muted">{t('hub.gioca.codice.avviso')}</p>
          <Button variant="secondary" fullWidth data-azione="copia" onClick={() => void copia()}>
            {esito === 'ok' ? t('hub.gioca.codice.copiato') : t('hub.gioca.codice.copia')}
          </Button>
          <p role="status" aria-live="polite" className={esito === 'ko' ? 'text-xs text-fg-muted' : 'sr-only'}>
            {esito === 'ko' ? t('hub.gioca.codice.copiaFallita') : esito === 'ok' ? t('hub.gioca.codice.copiato') : ''}
          </p>
        </>
      )}
      <Button variant="primary" fullWidth data-azione="vai" onClick={onVai}>
        {t('hub.gioca.codice.vai')}
      </Button>
    </div>
  );
}
